import { InitFilterValueConverter } from "./init-filter";
import * as Enumerable from 'linq';
import "moment";
import * as moment from "moment";
import { DurationInputArg2 } from 'moment';
import { UserModel } from "../../models/user.models";
import { DataType, QueryFilter, QueryLookupsWithContext } from "../../models/queries.models";
import { DataGridColumn, RowItem } from "../../components/comp-data-grid/comp-data-grid.models";
import { CloneHelper } from "../../helpers/clone.helper";
import { LookupValueRequest, LookupValueResponse } from "../../models/lookup.models";
import { StringHelper } from "../../helpers/string.helper";
import { DateExpressionParser } from "../../helpers/date-expressions.helper";
import { NumberHelper } from "../../helpers/number.helper";
import { DateTimeHelper } from "../../helpers/date-time.helper";

export class QueryFiltersExecutor {

    private _user: UserModel;
    private _filters: Array<QueryFilter>;
    private _sortingColumns: DataGridColumn[];
    private _page: number = 0;
    private _pageCount: number = 100;

    constructor(private initFilter: InitFilterValueConverter,
        filters: Array<QueryFilter>,
        page: number,
        pageCount: number,
        sortingColumns: DataGridColumn[],
        user: UserModel) {
        this._filters = CloneHelper.cloneObject(filters);
        this._page = page;
        this._pageCount = pageCount;
        this._sortingColumns = sortingColumns;
        this._user = user;
    }

    public static getMinifiedJsonForQueryFilters(filters: QueryFilter[]): string {

        //The Mozilla docs say to return undefined (instead of "none"):
        //http://jsfiddle.net/userdude/rZ5Px/

        function replacer(key, value) {
            if (key == "initFilterData" ||
                key == "supportedTypes" ||
                key == "supportedTypesForCollectionProperties") return undefined;
            else return value;
        }

        return JSON.stringify(filters, replacer);
    }

    public static filterCollection(itemsCollection: RowItem[], columns: DataGridColumn[]): RowItem[] {
        let filtersToInclude: QueryFilter[] = [];
        columns.forEach(x => {
            if (x.filtersResolver) {
                const filters = x.filtersResolver(x.filters);
                if (filters) {
                    filtersToInclude = filtersToInclude.concat(filters);
                }
            } else {
                if (x.filters) {
                    filtersToInclude = filtersToInclude.concat(x.filters);
                }
            }
        });
        itemsCollection = itemsCollection && filtersToInclude && filtersToInclude.length > 0
            ? itemsCollection.filter(rowItem => QueryFiltersExecutor.areConditionsSatisfied(rowItem, rowItem.item, filtersToInclude))
            : itemsCollection;
        return itemsCollection;
    }

    public static getValue(item: QueryFilter, user: UserModel) {
        let value;
        if (typeof item.filterValue == "function") {
            value = item.filterValue();
        } else {
            value = item.filterValue;
        }

        if (Array.isArray(value) && value.length > 0) {
            const stringValues = value.map(v => typeof (v) === 'string' ? `'${v}'` : v)
            return stringValues.join(',');
        }

        if (item.filterProperty.type == DataType.Default && value) {
            value = value ? value.toString() : value;
            const find = "'";
            const re = new RegExp(find, 'g');

            value = value.replace(re, "''");
        }

        const tags = (item.filterProperty.tags || "").toLowerCase();
        if (tags.split(",").indexOf("useremail") > -1 && value && value.toLowerCase() == "me") {
            value = user.user.email;
        }
        else if (tags.split(",").indexOf("userfullname") > -1 && value && value.toLowerCase() == "me") {
            value = user.user.fullName;
        }

        return value;
    }

    public getUri(skipPagingAndSorting: boolean,
        includeCount: boolean = true,
        baseUri: string = null, limit: number = -1): Promise<string> {
        return new Promise<string>((resolve) => {

            const hierarchicalInFilters = this._filters.filter(x => x.filterOperator && ["in", "not in"].indexOf(x.filterOperator.friendlyName) > -1 && x.filterProperty && x.filterProperty.type == DataType.HierarchicalLookup);

            const lookupsToLoad = Enumerable.from(hierarchicalInFilters).select(x => x.filterProperty.lookupMappedToProperty).distinct().toArray();

            const promises: Promise<LookupValueResponse>[] = [];
            lookupsToLoad.forEach(lookup => {
                promises.push(this.initFilter.lookupClientService.getLookup(new LookupValueRequest(lookup)));
            });

            Promise.all(promises).then(results => {

                hierarchicalInFilters.forEach(filter => {
                    const lookup = results.filter(z => z.lookupValueQuery == filter.filterProperty.lookupMappedToProperty)[0];

                    let notFound = true;
                    let values = lookup.allValuesIncludingInactive;
                    while (notFound) {

                        if (values.length == 0) {
                            notFound = false;
                        } else {
                            const value = values.filter(x => x.id == filter.filterValue)[0];
                            if (!value) {
                                values = Enumerable.from(values).selectMany(x => x.children).toArray();
                            } else {
                                const allIds = [];
                                allIds.push(value.id);
                                let children = value.children;
                                while (children.length > 0) {
                                    allIds.push(...children.map(x => x.id));
                                    children = Enumerable.from(children).selectMany(x => x.children).toArray();
                                }
                                notFound = false;
                                filter.filterValue = allIds;
                            }
                        }
                    }
                })

                let uri = "";
                //first we should group the filter with their group
                const groupedFilters = Enumerable.from(this._filters).where(x => x.filterProperty != null)
                    .groupBy((filter) => filter.groupGuid, (filter) => filter)
                    .toArray();

                const handleGroups = (groupedFilters: Enumerable.IGrouping<string, QueryFilter>[], isSubGroup: boolean = false) => {

                    let outerCounter = 0;
                    let groupsFullUri = "";

                    for (const group of groupedFilters) {
                        if (outerCounter !== 0 && groupsFullUri) {
                            const item: QueryFilter = group.toArray()[0];
                            const val = QueryFiltersExecutor.getValue(item, this._user);
                            if ((item.skipOnNullOrEmpty && item.filterOperator.requiresFilterValue && (val == null || val.toString() == "")) ||
                                (!item.filterProperty.isNullable && item == null) ||
                                (val === undefined && item.filterProperty.type == DataType.Boolean)) {
                                continue;
                            }
                            groupsFullUri += (item.isAnd ? " and" : " or");
                        }
                        let innerCounter = 0;
                        if (group.toArray().length > 0) {
                            let groupUri = "(";

                            const subGroups = Enumerable.from(group.toArray()).where(x => x.filterProperty != null && x.subGroupGuid != null)
                                .groupBy((filter) => filter.subGroupGuid, (filter) => filter)
                                .toArray();
                            if (subGroups.length > 0 && !isSubGroup) {
                                groupUri += handleGroups(subGroups, true);
                            }
                            else {
                                for (const item of group.toArray()) {
                                    const val = QueryFiltersExecutor.getValue(item, this._user);
                                    //if this is not the last item in this group we append and / or
                                    if ((item.skipOnNullOrEmpty && item.filterOperator.requiresFilterValue &&
                                        (val == null || val.toString() == "")) ||
                                        (!item.filterProperty.isNullable && item == null) ||
                                        (val === undefined && item.filterProperty.type == DataType.Boolean)) {
                                        continue;
                                    }
                                    if (innerCounter !== 0) {
                                        groupUri += (item.isAnd ? " and" : " or");
                                    }
                                    groupUri += this.getUriPart(item);
                                    innerCounter++;
                                }
                            }

                            //close bracket for group
                            groupUri += ")";

                            if (groupUri != '()') {
                                groupsFullUri += groupUri;
                            }
                        }
                        innerCounter = 0;
                        //if this is not the last item then we need to add an And / Or

                        outerCounter++;
                    }

                    return groupsFullUri;
                }

                uri += handleGroups(groupedFilters);

                //this must be refactored
                if (uri && uri !== "" && uri !== "()") {

                    let fullUrl = (baseUri && baseUri.indexOf("?") > 0 ? "&" : "?") + "$filter=";
                    fullUrl = (baseUri && baseUri.indexOf("$filter") > 0) ? "" : fullUrl;
                    //commented this out because it was doing $filter= and (....) which was breaking
                    // if (baseUri && StringUtilities.endsWith(baseUri, ')')) {
                    //     fullUrl += " and ";
                    // }
                    uri = fullUrl + "(" + uri + ")";
                } else {
                    let fullUrl = (baseUri && baseUri.indexOf("?") > 0 ? "" : "?");
                    if (baseUri && StringHelper.endsWith(baseUri, ')') && uri !== "()" && uri !== "") {
                        fullUrl += " and ";
                    }
                    uri = fullUrl + (uri !== "()" ? uri : "");
                }

                if (!skipPagingAndSorting) {
                    //add ordering
                    //add paging
                    uri += "&$top=" + (this._pageCount != null && this._pageCount !== undefined ? (this._pageCount || 100) : 100);
                    uri += this._page != null ? "&$skip=" + ((this._pageCount ? this._pageCount : 0) * (this._page ? this._page : 0)) : "";

                    const sorting = Enumerable.from(this._sortingColumns).where(x => (x.isAscending != null || x.isGrouping)).orderBy(x => x.sortOrder).orderBy(x => x.isGrouping)
                        .distinct(x => x.name)
                        .toArray();
                    if (sorting && sorting.length > 0) {
                        uri += "&$orderby=";
                        for (const order of sorting) {
                            const name = this.getPropertyNameForOrdering(order);
                            uri += name + " " + (order.isAscending ? "asc" : "desc") + " ,";
                        }

                        uri = uri.substring(0, uri.length - 1);
                    }

                    if (includeCount) {
                        uri += "&$count=true";
                    }

                    const grouping = Enumerable.from(this._sortingColumns).where(x => (x.isGrouping == true)).orderBy(x => x.isGrouping)
                        .toArray();

                    if (grouping && grouping.length > 0) {
                        let index = uri.indexOf("?");
                        index = index > -1 ? index : 0;
                        const uriSubstring = index > -1 ? uri.substring(index, uri.length) : null;
                        uri = "/Genesis.GroupBy(column='";
                        uri += grouping[0].name + "',propertyForAggregation=null,aggregationType=null,groupLimit=" + (limit > 0 ? limit : 100) + ")";

                        const queryIndex = baseUri.indexOf("?");

                        if (queryIndex > -1) {

                            if (baseUri.substring(queryIndex, baseUri.length).length > 1) {
                                const filterStr = baseUri.substring(queryIndex, baseUri.length);

                                uri += filterStr;
                            }

                            baseUri = baseUri.substring(0, queryIndex);
                        }

                        if (uriSubstring)
                            uri += (uriSubstring.indexOf("?") > -1 || uri.indexOf("?") > -1) ? uriSubstring : "?" + uriSubstring;

                        const topIndex = uri.indexOf("&$top");
                        const skipIndex = uri.indexOf("&$skip");

                        //remove $top if a limit is entered for grouping
                        if (topIndex > 0 && limit > 0) {
                            const partToRemove = uri.slice(topIndex, skipIndex);
                            uri = uri.replace(partToRemove, "");
                        }
                    }
                }

                if (baseUri && uri && baseUri.indexOf("$filter") != -1 && uri[0] == "(") {
                    baseUri += " and ";
                }

                resolve(baseUri ? baseUri.concat(uri) : uri);
            });
        });
    }

    private getUriPart(item: QueryFilter): string {
        let result = "";

        if (item.filterOperator) {
            result += " ";
            if (item.filterOperator.requiresInterpolation && item.filterOperator.requiresInterpolation.toString() == "true") {
                if (item.filterProperty.lookupMappedToProperty && item.filterProperty.isCollection && item.filterProperty.type == DataType.Default) {
                    return this.getExpressionForLookupString(item);
                } else {
                    result += item.filterOperator.translatedSymbol.replace(/\{0\}/g, this.getPropertyName(item))
                        .replace(/\{1\}/g, this.getFilterValueForUri(item, this._user))
                        .replace(/\{2\}/g, this.getFilterValueForUri(item, this._user));

                    //For equal and not equal operator, we need to check for null values as well
                    //example for eq: dynamicProperty(jsonData,'dynProp') eq '' or dynamicProperty(jsonData,'dynProp') eq null
                    //example for ne : dynamicProperty(jsonData,'dynProp') ne '' and dynamicProperty(jsonData,'dynProp') ne null
                    if (item.filterProperty.isDynamicProperty &&
                        ["=", "<>"].indexOf(item.filterOperator.friendlyName) >= 0 &&
                        item.filterValue !== 0 &&
                        (item.filterValue == false ||
                            item.filterValue == null ||
                            item.filterValue == "")) {
                        let nullResult = item.filterOperator.translatedSymbol.replace(/\{0\}/g, this.getPropertyName(item))
                            .replace(/\{1\}/g, null)
                            .replace(/\{2\}/g, null);
                        nullResult = nullResult.replace("'null'", "null");
                        result = '(' + result + (item.filterOperator.friendlyName == "=" ? ' or ' : ' and ') + nullResult + ")";
                    }
                }
            } else {
                if (item.filterProperty.lookupMappedToProperty && item.filterProperty.isCollection) {
                    return this.getExpressionForLookupString(item);
                }
                else if (item.filterProperty.type == DataType.Date || item.filterProperty.type == DataType.DateTime) {
                    result += this.getDateUriPart(item);
                } else if (item.filterProperty.isDynamicProperty &&
                    item.filterValue !== 0 &&
                    (item.filterValue == false ||
                        item.filterValue == null ||
                        item.filterValue == "")) {
                    return `(${this.getPropertyName(item)} ${item.filterOperator.translatedSymbol} ${this.getFilterValueForUri(item, this._user)} or ` +
                        `${this.getPropertyName(item)} ${item.filterOperator.translatedSymbol} null)`;
                } else {

                    result += `(${this.getPropertyName(item)} ${item.filterOperator.translatedSymbol} ${this.getFilterValueForUri(item, this._user)})`;

                }
            }
        }
        return result;
    }

    private getPropertyNameForOrdering(item: DataGridColumn) {
        const property = item;
        let result = property.name;

        if (property.isDynamicProperty && !property.skipCollate) {
            if (property.dataType == DataType.Default) {
                result = `dynamicPropertyCollate(jsonData,'${item.name}', '_CS_AS')`
            }
            else if (property.dataType == DataType.Date || property.dataType == DataType.DateTime) {
                result = `dynamicPropertyDate(jsonData,'${item.name}')`
            }
            else if (property.dataType == DataType.Number ||
                property.dataType == DataType.Lookup ||
                property.dataType == DataType.HierarchicalLookup) {
                result = `dynamicPropertyInt(jsonData,'${item.name}')`
            }
            else if (property.dataType == DataType.Double) {
                result = `dynamicPropertyDouble(jsonData,'${item.name}')`
            }
            else if (property.dataType == DataType.Boolean) {
                result = `dynamicPropertyBool(jsonData,'${item.name}')`
            }
            return result;
        }

        if (property.dataType == DataType.Default && !property.skipCollate) {
            result = `collate(${result},'_CS_AS')`;
        }

        return result;
    }

    private getPropertyName(item: QueryFilter) {
        const property = item.filterProperty;
        if (property.isDynamicProperty) {
            if (property.type == DataType.Default) {
                return `dynamicPropertyString(jsonData,'${property.name}')`
            }
            else if (property.type == DataType.Date || property.type == DataType.DateTime) {
                return `dynamicPropertyDate(jsonData,'${property.name}')`
            }
            else if (property.type == DataType.Number ||
                property.type == DataType.Lookup ||
                property.type == DataType.HierarchicalLookup ||
                property.type == DataType.EntityFinder) {
                return `dynamicPropertyInt(jsonData,'${property.name}')`
            }
            else if (property.type == DataType.Double) {
                return `dynamicPropertyDouble(jsonData,'${property.name}')`
            }
            else if (property.type == DataType.Boolean) {
                return `dynamicPropertyBool(jsonData,'${property.name}')`
            }
        } else {
            return property.name;
        }
    }

    private getExpressionForLookupString(item: QueryFilter) {

        const eqPart = ("{0} eq '{1}'")
            .replace(/\{0\}/g, item.filterProperty.name)
            .replace(/\{1\}/g, this.getFilterValueForUri(item, this._user));
        const nePart = ("{0} ne '{1}'")
            .replace(/\{0\}/g, item.filterProperty.name)
            .replace(/\{1\}/g, this.getFilterValueForUri(item, this._user));
        const startsWithPart = ("startswith({0}, '{1}')")
            .replace(/\{0\}/g, item.filterProperty.name)
            .replace(/\{1\}/g, this.getFilterValueForUri(item, this._user) + ',');
        const containsWithPart = ("contains({0}, '{1}')")
            .replace(/\{0\}/g, item.filterProperty.name)
            .replace(/\{1\}/g, ',' + this.getFilterValueForUri(item, this._user) + ',');
        const endsWithPart = ("endswith({0}, '{1}')")
            .replace(/\{0\}/g, item.filterProperty.name)
            .replace(/\{1\}/g, ',' + this.getFilterValueForUri(item, this._user));

        switch (item.filterOperator.translatedSymbol) {
            case "startswith({0}, '{1}')": {
                return ` (${startsWithPart} or ${eqPart}) `;
            }
            case "contains({0}, '{1}')":
            case "{0} in({1})": {
                return ` (${endsWithPart} or ${containsWithPart} or ${startsWithPart} or ${eqPart}) `;
            }
            case "endswith({0}, '{1}')": {
                return ` (${endsWithPart} or ${eqPart}) `;
            }
            case "{0} eq '{1}'":
            case "eq":
                {
                    return ` (${eqPart}) `;
                }
            case "{0} ne '{1}'":
            case "ne":
                {
                    return ` (${nePart}) `;
                }
            default:
                return item.filterOperator.translatedSymbol.replace(/\{0\}/g, this.getPropertyName(item))
                    .replace(/\{1\}/g, this.getFilterValueForUri(item, this._user))
                    .replace(/\{2\}/g, this.getFilterValueForUri(item, this._user));
        }
    }

    private getDateUriPart(item: QueryFilter, isSecondValue: boolean = false) {
        const value = QueryFiltersExecutor.getValue(item, this._user);
        const propName = this.getPropertyName(item);

        if (value != null && value !== "") {
            let date = this.parseDate(value, isSecondValue, item.filterProperty.type);

            const dateGranularity: DurationInputArg2 = this.getDateGranularity(item.filterProperty.format);

            //returning the date time part if it is date time and the expression is not a day type example today where time needs to be ignored and query is for entire day 
            if (item.filterProperty.type === DataType.DateTime &&
                !DateExpressionParser.isDateExpression(value) &&
                (dateGranularity == 'seconds' || dateGranularity == 'milliseconds')
            ) {
                return this.getPropertyName(item) +
                    " " +
                    item.filterOperator.translatedSymbol +
                    " " +
                    date.toJSON();
            }

            const isFormatRangeExpression = item && item.filterProperty && item.filterProperty.format
                && DateExpressionParser.isExpressionDateRange(item.filterProperty.format, true) && !DateExpressionParser.isDateExpression(value);

            if (isFormatRangeExpression) {
                // make sure date is start of range, using the format as key and the current value as data to be passed to the expression
                date = this.parseDate(item.filterProperty.format, isSecondValue, item.filterProperty.type, value, true);
            }

            const endDateForRangeExpression = isFormatRangeExpression ? DateExpressionParser.getEndDateFromRangeExpression(item.filterProperty.format, date, true) : DateExpressionParser.getEndDateFromRangeExpression(value, date);

            const now = moment(moment(date).format(item.filterProperty.format), item.filterProperty.format);
            const timeOffsetInMinutes = item.filterProperty.type === DataType.DateTime ? -now.utcOffset() : 0;
            const dateMomentForIneq = now.add(timeOffsetInMinutes, 'minutes');
            const dateForIneqPlusOneDay = dateMomentForIneq.clone().add(1, dateGranularity);
            const dateForIneq = dateMomentForIneq.toDate();
            let dateTo = dateForIneqPlusOneDay.toDate();
            if ((isFormatRangeExpression || DateExpressionParser.isExpressionDateRange(value)) && endDateForRangeExpression != null) {
                dateTo = moment(endDateForRangeExpression).add(timeOffsetInMinutes, 'minutes').clone().toDate();
            }
            const dateStr = `${dateForIneq.getFullYear()}-${NumberHelper.pad2(dateForIneq.getMonth() + 1)}-${NumberHelper.pad2(dateForIneq.getDate())}T${NumberHelper.pad2(dateForIneq.getHours())}:${NumberHelper.pad2(dateForIneq.getMinutes())}:${NumberHelper.pad2(dateForIneq.getSeconds())}Z`;
            const dateToStr = `${dateTo.getFullYear()}-${NumberHelper.pad2(dateTo.getMonth() + 1)}-${NumberHelper.pad2(dateTo.getDate())}T${NumberHelper.pad2(dateTo.getHours())}:${NumberHelper.pad2(dateTo.getMinutes())}:${NumberHelper.pad2(dateTo.getSeconds())}Z`;
            const neDateFilter = `(${propName} lt ${dateStr} or ${propName} gt ${dateToStr})`;
            const eqDateFilter = `(${propName} ge ${dateStr} and ${propName} le ${dateToStr})`;
            const ltDateFilter = `(${propName} lt ${dateStr})`;
            const gtDateFilter = `(${propName} gt ${dateToStr})`;
            const geDateFilter = `(${propName} ge ${dateStr})`;
            const leDateFilter = `(${propName} le ${dateToStr})`;

            if (item.filterProperty.type === DataType.Date || item.filterProperty.type === DataType.DateTime) {
                switch (item.filterOperator.translatedSymbol) {
                    case "eq":
                        return eqDateFilter;
                    case "ne":
                        return neDateFilter;
                    case "lt":
                        return ltDateFilter;
                    case "gt":
                        return gtDateFilter;
                    case "ge":
                        return geDateFilter;
                    case "le":
                        return leDateFilter;
                    default:
                        return dateStr;
                }
            }
        } else {
            return `(${propName} ${item.filterOperator.translatedSymbol} null)`;
        }
    }

    private getDateGranularity = (dateFormat) => {
        if (!dateFormat) {
            return "seconds";
        }
        if (dateFormat.indexOf('f') !== -1) {
            return "milliseconds";
        }
        if (dateFormat.indexOf('s') !== -1) {
            return "seconds";
        }
        if (dateFormat.indexOf('m') !== -1) {
            return "minutes";
        }
        if (dateFormat.toLowerCase().indexOf('h') !== -1) {
            return "hours";
        }
        if (dateFormat.toLowerCase().indexOf('d') !== -1) {
            return "days";
        }
    }

    private parseDate(value: string | Date, isSecondValue: boolean = false, dataType: DataType, data: any = null, matchByKey: boolean = false): Date {
        const exp = value ? DateExpressionParser.getDateValue(value.toString(), data, matchByKey) : undefined;
        if (value && typeof value === "string" && exp) {
            const expDate = exp;
            //if data type is date or the expression is a day type example today and yesterday set time 00:00:00
            if (dataType == DataType.Date || DateExpressionParser.isDateExpression(value)) expDate.setHours(0, 0, 0);
            value = expDate;
        }
        let date: Date = null;
        if (typeof value == "string") {
            date = new Date(value);
        } else {
            date = value;
        }
        if (isSecondValue) {
            date.setDate(date.getDate() + 1);
        }
        return date;
    }

    private getFilterValueForUri(item: QueryFilter, user: UserModel): string {

        let value = QueryFiltersExecutor.getValue(item, user);

        //in this case it is a Lookup
        if ((item.filterProperty.type === DataType.Number ||
            item.filterProperty.type === DataType.Double) &&
            value &&
            ["-", "+"].indexOf(value.toString().trim()) > -1) {
            return null;
        }

        //in this case it is a Lookup
        if (item.filterProperty.type === DataType.Lookup ||
            item.filterProperty.type === DataType.HierarchicalLookup ||
            item.filterProperty.type === DataType.EntityFinder) {
            if (value == null) {
                if (!item.filterProperty.isNullable) {
                    return "0";
                }
                return null;
            }

            value = value + "";

            const currentUser = this._user;
            const contextProperty = QueryLookupsWithContext.filter(x => x.lookup.toLowerCase() == (item.filterProperty.lookupMappedToProperty || '').toLowerCase())[0];
            if (contextProperty && item.filterContextPropertyValue && value == item.filterContextPropertyValue) {
                value = currentUser[contextProperty.contextProperty];
            }
            return value;
        }

        if (typeof value == "string") {
            value = encodeURIComponent(value);
        }

        if (!item.filterOperator.requiresInterpolation && item.filterProperty.type == DataType.Default) {
            value = `'${value}'`;
        }

        value = value || !this.initFilter
            ? value
            : this.initFilter.convert(item.filterValue, {
                type: item.filterProperty.type,
                contextPropertyValue: item.filterContextPropertyValue,
                lookup: item.filterProperty.lookupMappedToProperty,
                tags: item.filterProperty.tags
            });

        if (value === undefined) {
            value = null;
        }

        return value;
    }

    private static areConditionsSatisfied(rowItem: RowItem, item: any, filters: QueryFilter[]): boolean {
        const groupedFilters = Enumerable.from(filters).groupBy(x => x.groupGuid);
        let response: boolean = true;
        groupedFilters.forEach(filterGroup => {
            let groupResponse: boolean = true;
            filterGroup.forEach(filter => {
                if (filter.isAnd) {
                    groupResponse = groupResponse && QueryFiltersExecutor.propertyComparison(rowItem, item, filter);
                }
                else {
                    groupResponse = groupResponse || QueryFiltersExecutor.propertyComparison(rowItem, item, filter);
                }
            });
            response = response && groupResponse;
        });
        return response;
    }

    private static unformat(value, decimal) {

        // Fails silently (need decent errors):
        value = value || 0;

        // Return the value as-is if it's already a number:
        if (typeof value === "number") return value;

        // Default decimal point comes from settings, but could be set to eg. "," in opts:
        decimal = decimal || ".";

        // Build regex to strip out everything except digits, decimal point and minus sign:
        const regex = new RegExp("[^0-9-" + decimal + "]", "g"),
            unformatted = parseFloat(
                ("" + value)
                    .replace(/\((?=\d+)(.*)\)/, "-$1") // replace bracketed values with negatives
                    .replace(regex, '')         // strip out any cruft
                    .replace(decimal, '.')      // make sure decimal point is standard
            );

        // This will fail silently which may cause trouble, let's wait and see:
        return !isNaN(unformatted) ? unformatted : 0;
    }

    private static propertyComparison(rowItem: RowItem, item: any, filter: QueryFilter): boolean {
        const operation = filter.filterOperator;
        const valueConverter = rowItem.columnKeys[filter.filterProperty.name] && rowItem.columnKeys[filter.filterProperty.name].column
            ? rowItem.columnKeys[filter.filterProperty.name].column.valueConverter
            : null;

        const propertyValueFromItem = this.getPropertyValue(item, filter.filterProperty.name);

        let propertyValue: string | number | Date = valueConverter ? valueConverter(propertyValueFromItem, item) : propertyValueFromItem;
        let filterValue: string | number | Date = filter.filterValue;
        //Here we convert all numbers to double to be able to compare numbers (e. int cannot be compared to long)
        switch (filter.filterProperty.type) {
            case DataType.Number:
            case DataType.Double:
                propertyValue = propertyValue == null ? null : this.unformat(propertyValue.toString(), null);
                filterValue = filter.filterValue !== null && this.isNumeric(filter.filterValue) ? this.unformat(filter.filterValue, null) : null;
                break;
            case DataType.Date:
            case DataType.DateTime:
                if (!(propertyValue instanceof Date) && typeof propertyValue == "string") {
                    propertyValue = new Date(propertyValue);
                }
                if (!(filterValue instanceof Date) && typeof filterValue == "string") {
                    filterValue = DateTimeHelper.castDate(filterValue);
                }
                if (filterValue instanceof Date) {
                    filterValue = DateTimeHelper.castDate(filterValue).getTime();
                }
                if (propertyValue instanceof Date) {
                    propertyValue = DateTimeHelper.castDate(propertyValue).getTime();
                }
                break;
            case DataType.Default:
                break;
        }

        if (((filterValue == null || filterValue.toString() == "") && filter.skipOnNullOrEmpty) || (filterValue === undefined && filter.filterProperty.type == DataType.Boolean)) {
            return true;
        }

        if (propertyValue && typeof propertyValue == "string") {
            propertyValue = propertyValue.toLowerCase();
        }

        if (typeof filterValue == "string") {
            filterValue = filterValue.toLowerCase();
        }
        switch (operation.translatedSymbol) {
            case "=":
            case "eq":
                return (propertyValue == null && propertyValue == filterValue) || (propertyValue != null && (propertyValue == filterValue));
            case "<>":
            case "ne":
                return (propertyValue == null && propertyValue != filterValue) || (propertyValue != null && (propertyValue != filterValue));
            case "<":
            case "lt":
                return propertyValue != null && (propertyValue < filterValue);
            case "<=":
            case "le":
                return propertyValue != null && (propertyValue <= filterValue);
            case ">":
            case "gt":
                return propertyValue != null && propertyValue > filterValue;
            case ">=":
            case "ge":
                return propertyValue != null && propertyValue >= filterValue;
            case "endswith({0}, '{1}')":
                return propertyValue != null && StringHelper.endsWith(propertyValue.toString(), filterValue.toString());
            case "startswith({0}, '{1}')":
                return propertyValue != null && StringHelper.startsWith(propertyValue.toString(), filterValue.toString());
            case "contains({0}, '{1}')":
                return propertyValue != null && propertyValue.toString().indexOf(filterValue.toString()) > -1;
            default:
                return true;
        }
    }

    private static isNumeric(str: string | number) {
        if (typeof str == "number") {
            return !isNaN(str);
        }
        if (typeof str == "string") {
            return !isNaN(parseFloat(str));
        }
        return false;
    }

    private static getPropertyValue(item: any, property: string): any {
        const props: string[] = property.split(".");
        if (props.length === 1) {
            return item ? item[property] : null;
        }
        const value = QueryFiltersExecutor.getPropertyValue(item[props[0]], property.substring(property.indexOf(".") + 1));
        return value;
    }
}
